'use client';

import { useEffect, useState } from 'react';

import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';

import { Button } from '@repo/ui/button';
import Icon from '@repo/ui/icon';

import { toClassName } from 'helpers/format';

type TProps = {
  locale: string;
};

export const cookieConsentGiven = (): 'yes' | 'no' | 'undecided' | string => {
  if (typeof window === 'undefined') return 'undecided';
  return localStorage.getItem('cookie_consent') ?? 'undecided';
};

const CookieConsent = ({ locale }: TProps) => {
  const { t } = useTranslation(locale);

  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hide, setHide] = useState(false);
  const [consentGiven, setConsentGiven] = useState<
    'yes' | 'no' | 'undecided' | string
  >('undecided');

  useEffect(() => {
    setIsMounted(true);
    const consent = cookieConsentGiven();
    setConsentGiven(consent);

    if (consent === 'undecided') {
      setIsOpen(true);
    }
  }, []);

  useEffect(() => {
    if (consentGiven !== 'undecided') {
      posthog.set_config({
        persistence: consentGiven === 'yes' ? 'localStorage+cookie' : 'memory',
      });
    }
  }, [consentGiven]);

  const handleClickAccept = () => {
    setIsOpen(false);
    localStorage.setItem('cookie_consent', 'yes');
    setConsentGiven('yes');
    setTimeout(() => setHide(true), 700);
    posthog.opt_in_capturing();
  };

  const handleClickDecline = () => {
    setIsOpen(false);
    localStorage.setItem('cookie_consent', 'no');
    setConsentGiven('no');
    setTimeout(() => setHide(true), 700);
    posthog.opt_out_capturing();
  };

  // Prevent hydration mismatch by not rendering until mounted
  if (!isMounted) return null;

  return (
    <div
      className={toClassName(
        'fixed z-[200] bottom-0 left-0 right-0 sm:left-4 sm:bottom-4 w-full sm:max-w-md duration-700',
        !isOpen
          ? 'transition-[opacity,transform] translate-y-8 opacity-0 pointer-events-none'
          : 'transition-[opacity,transform] translate-y-0 opacity-100 pointer-events-auto',
        hide && 'hidden',
      )}
    >
      <div className="m-3 shadow-lg bg-white dark:bg-black">
        <div className="grid gap-2">
          <div className="border-b h-14 flex items-center justify-between p-4 border-white/10">
            <h1 className="text-3xl font-medium">
              {t('component.cookieConsent.title')}
            </h1>
            <Icon accessor="cookie" className="h-[1.2rem] w-[1.2rem]" />
          </div>
          <div className="p-4">
            <p className="text-2xl font-normal text-start">
              {t('component.cookieConsent.description')}
              <br />
            </p>
          </div>
          <div className="flex gap-2 p-4 py-5 border-t border-white/10">
            <Button
              onClick={handleClickAccept}
              className="w-full !bg-white !text-black hover:!bg-white/80 duration-300 border border-white/10 !text-2xl uppercase hover:underline rounded-none border-none bg-none text-white"
            >
              {t('component.cookieConsent.label.accept')}
            </Button>
            <Button
              onClick={handleClickDecline}
              className="w-full bg-black text-white border border-white/10 hover:bg-white/10 duration-300 !text-2xl uppercase hover:underline rounded-none border-none bg-none"
              // variant="secondary"
            >
              {t('component.cookieConsent.label.decline')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
